import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  Stack,
  Link
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [devInfo, setDevInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    setDevInfo(null);

    try {
      // Same-origin /api so dev proxy and nginx forward to the backend (REACT_APP_API_URL may be an internal Docker hostname).
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Show dev info if available (only in development)
        if (data.dev) {
          setDevInfo(data);
        }
      } else {
        setError(data.error || 'Failed to send reset code');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout showNavbar={false}>
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 }, 
            width: '100%', 
            maxWidth: 450,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box 
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: 2,
                backgroundColor: 'primary.main',
                mb: 2
              }}
            >
              <EmailIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Forgot Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your email to receive a password reset code
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" gutterBottom>
                <strong>Check your email!</strong>
              </Typography>
              <Typography variant="body2">
                If an account exists with this email, a password reset code has been sent. 
                Please check your inbox and follow the instructions.
              </Typography>
              {devInfo && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    🔧 Development Mode
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    Code: <strong>{devInfo.code}</strong>
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                    Use this code on the reset password page
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {!success ? (
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  variant="outlined"
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 500
                    }
                  }}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </Stack>
            </form>
          ) : (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => navigate('/reset-password', { state: { email } })}
              sx={{ 
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Enter Reset Code
            </Button>
          )}

          <Box sx={{ textAlign: 'center', pt: 3, mt: 3, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              startIcon={<ArrowBackIcon />}
              sx={{
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Back to Login
            </Link>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default ForgotPassword;


